/**
 * Interaction Token Data (SSOT) -- HAND-AUTHORED.
 *
 * Canonical source for opacity and motion tokens. Consumed by the styles interaction-token leaves for
 * derived color/motion values, by `const/timing-tokens.ts` for the derived numeric/CSS duration maps, and directly by
 * the brand token leaves under brand/. Homed in const/ because those consumers span layers a `utils/` leaf cannot be
 * imported from: `const/timing-tokens.ts` derives its whole duration scale from `motion`, and const/ may value-import
 * only const/types/zod. It carries no AUTO-GENERATED banner because nothing generates it: a generator that reads THIS
 * file back as its own input is a fixed point with no authoritative upstream.
 *
 * AUTHORING CONSTRAINT: every semantic alias below MUST reference its scale entry (`hover: opacityScale.veryStrong`),
 * never restate the literal -- a restated literal silently drifts when the scale moves.
 */

// Opacity numeric scale (unique values only)
const opacityScale = {
	transparent: 0,
	minimal: 0.05,
	subtle: 0.1,
	light: 0.3,
	muted: 0.4,
	medium: 0.5,
	semiTransparent: 0.6,
	strong: 0.7,
	veryStrong: 0.8,
	nearOpaque: 0.9,
	opaque: 1,
} as const;

// Semantic aliases referencing the scale (no duplicated literals)
//
// NO `disabled` RUNG HERE, deliberately (DL-1.3, 2026-08-20). "The opacity of a disabled control" is ONE decision and it
// belongs to the interaction family -- `disabled` in brand/federa/tokens/interaction.ts, emitted as
// `--interaction-disabled-opacity` -- because that record also carries the `cursor` and `pointerEvents` halves of the
// same contract. This file previously spelled it a second time as `disabled: opacityScale.medium` (0.5) while the
// interaction record resolved to 0.4, so two disabled controls rendered at different opacities depending on which
// surface the consumer happened to import. Re-adding a `disabled` alias here restores that split -- reach for
// `disabled.opacity` instead.
export const opacity = {
	...opacityScale,
	hover: opacityScale.veryStrong,
	active: opacityScale.nearOpaque,
	loading: opacityScale.semiTransparent,
	overlay: opacityScale.semiTransparent,
	backdrop: opacityScale.strong,
} as const;

// Motion easing curves (unique values only)
const easingCurves = {
	standard: 'cubic-bezier(0.4, 0.0, 0.2, 1)',
	emphasized: 'cubic-bezier(0.2, 0.0, 0, 1)',
	/** Constant-rate curve. Belongs on the SCALE because a continuously rotating indicator must not accelerate, and a
	 *  spinner outside brand/ that can reach no token for it hardcodes the keyword instead. */
	linear: 'linear',
	/** Overshoot curve for playful entrances. On the SCALE (DL-1.4) like every easing curve: none is defined outside
	 *  this SSOT, the timing-token leaf (`const/timing-tokens.ts`) included. */
	spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
} as const;

// Motion duration scale (unique values only)
const durationScale = {
	fastest: '80ms',
	fast: '120ms',
	medium: '200ms',
	base: '250ms',
	slow: '320ms',
	slower: '500ms',
	slowest: '800ms',
} as const;

export const motion = {
	easing: {
		...easingCurves,
		smooth: easingCurves.standard,
	},
	duration: {
		...durationScale,
		normal: durationScale.base,
	},
} as const;
